import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import managementService from '../services/managementService';
import { RoleMetricConfig, MetricDefinition } from '../types/prompt';
import * as promptApi from '../services/promptManagementService';

/**
 * Tenant-configurable display labels keyed by metric_key. The tenant's
 * prompt config (and `tenant.metric_labels`) drive what keys exist —
 * `metric_1/2/3/drink_promotion` are no longer assumed.
 */
export type MetricLabels = Record<string, string>;

// Empty default — tenants without metric_labels render with whatever the
// prompt template provides (via fetchVersionConfigs) or no labels at all.
// We deliberately do NOT fall back to SAJJ strings: ghost-named columns are
// worse than blank ones because they misrepresent the tenant.
const DEFAULT_METRIC_LABELS: MetricLabels = {};

interface Tenant {
  tenant_id: string;
  tenant_name: string;
  description?: string;
  created: string;
  metric_labels?: MetricLabels;
}

function parseMetricLabels(raw: any): MetricLabels | undefined {
  if (!raw) return undefined;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (typeof raw === 'object') return raw;
  return undefined;
}

interface TenantContextType {
  selectedTenant: string | null;
  setSelectedTenant: (tenantId: string | null) => Promise<void>;
  tenantName: string;
  tenant: Tenant | null;
  isLoading: boolean;
  metricLabels: MetricLabels;
  roleMetricConfigs: Record<string, RoleMetricConfig>;
  getMetricConfigForRole: (role: string | null) => RoleMetricConfig | null;
  versionMetricConfigs: Record<string, RoleMetricConfig>;
  fetchVersionConfigs: (versionIds: string[]) => Promise<void>;
  getConfigForShift: (shift: { prompt_template_version_id?: string; role?: string | null }) => RoleMetricConfig | null;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: React.ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [selectedTenant, setSelectedTenantState] = useState<string | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedTenantIdRef = useRef<string | null>(null);
  const [roleMetricConfigs, setRoleMetricConfigs] = useState<Record<string, RoleMetricConfig>>({});
  const fetchedRoleConfigsTenantRef = useRef<string | null>(null);
  const [versionMetricConfigs, setVersionMetricConfigs] = useState<Record<string, RoleMetricConfig>>({});

  // Get tenant name for display
  const tenantName = tenant?.tenant_name || 'Select Tenant';

  // Set initial tenant based on user role
  useEffect(() => {
    if (user?.managementUser) {
      const { role: rawRole, tenant_id } = user.managementUser;
      // Normalize role to handle case-insensitive comparison
      const role = rawRole ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase() : null;
      
      // Reset the fetched tenant ref when user changes
      fetchedTenantIdRef.current = null;
      
      if (role === 'Admin') {
        // Admins start with no tenant selected (unless restored from localStorage)
        // Only set to null if no tenant is currently selected to avoid overriding user selection
        if (!selectedTenant) {
          setSelectedTenantState(null);
          setTenant(null);
        }
      } else if (role === 'Corporate' || role === 'Manager') {
        // Corporate and store managers are automatically assigned to their tenant
        if (tenant_id) {
          // Always set selectedTenant to their tenant_id (even if already set, to ensure consistency)
          if (selectedTenant !== tenant_id) {
            setSelectedTenantState(tenant_id);
          }
        }
      }
    }
  }, [user?.managementUser]);

  // Fetch tenant details when selectedTenant is set for corporate/store managers
  useEffect(() => {
    const fetchTenantDetails = async () => {
      if (!user?.managementUser) return;
      
      const { role: rawRole, tenant_id } = user.managementUser;
      // Normalize role to handle case-insensitive comparison
      const role = rawRole ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase() : null;
      
      // Fetch for Admin users (any tenant) or Corporate/Manager users (their own tenant)
      if (!selectedTenant) return;
      const shouldFetch = role === 'Admin'
        || ((role === 'Corporate' || role === 'Manager') && selectedTenant === tenant_id);
      if (shouldFetch) {
        // Only fetch if we haven't already fetched for this tenant_id
        if (fetchedTenantIdRef.current !== selectedTenant) {
          setIsLoading(true);
          try {
            // Try getTenantById first
            const response = await managementService.getTenantById(selectedTenant);
            if (response.success && response.tenant) {
              setTenant({
                tenant_id: response.tenant.tenant_id,
                tenant_name: response.tenant.tenant_name,
                description: response.tenant.description,
                created: response.tenant.created,
                metric_labels: parseMetricLabels(response.tenant.metric_labels)
              });
              fetchedTenantIdRef.current = selectedTenant;
            } else {
              // Fallback: use getTenants and filter
              const tenantsResponse = await managementService.getTenants();
              if (tenantsResponse.success && tenantsResponse.tenants) {
                const foundTenant = tenantsResponse.tenants.find(t => t.tenant_id === selectedTenant);
                if (foundTenant) {
                  setTenant({
                    tenant_id: foundTenant.tenant_id,
                    tenant_name: foundTenant.tenant_name,
                    description: foundTenant.description,
                    created: foundTenant.created,
                    metric_labels: parseMetricLabels(foundTenant.metric_labels)
                  });
                  fetchedTenantIdRef.current = selectedTenant;
                } else {
                  console.error('Tenant not found in getTenants response');
                  // Fallback: create a basic tenant object
                  setTenant({
                    tenant_id: selectedTenant,
                    tenant_name: `Tenant ${selectedTenant}`,
                    description: '',
                    created: new Date().toISOString()
                  });
                  fetchedTenantIdRef.current = selectedTenant;
                }
              } else {
                console.error('Error fetching tenants:', tenantsResponse.error);
                // Fallback: create a basic tenant object
                setTenant({
                  tenant_id: selectedTenant,
                  tenant_name: `Tenant ${selectedTenant}`,
                  description: '',
                  created: new Date().toISOString()
                });
                fetchedTenantIdRef.current = selectedTenant;
              }
            }
          } catch (error) {
            console.error('Error fetching tenant details:', error);
            // Fallback: create a basic tenant object
            setTenant({
              tenant_id: selectedTenant,
              tenant_name: `Tenant ${selectedTenant}`,
              description: '',
              created: new Date().toISOString()
            });
            fetchedTenantIdRef.current = selectedTenant;
          } finally {
            setIsLoading(false);
          }
        }
      }
    };

    fetchTenantDetails();
  }, [selectedTenant, user?.managementUser]);

  // Persist selected tenant in localStorage.
  // Skip the initial-mount fire when the value is still null: the restore
  // effect below waits for auth to load, so a transient null here would
  // otherwise wipe the saved tenant before it can be restored — which
  // looks to the user like the tenant was forgotten on reload.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      if (selectedTenant === null) return;
    }
    if (selectedTenant) {
      localStorage.setItem('selectedTenant', selectedTenant);
    } else {
      localStorage.removeItem('selectedTenant');
    }
  }, [selectedTenant]);

  // Load selected tenant from localStorage on mount
  useEffect(() => {
    const savedTenant = localStorage.getItem('selectedTenant');
    
    if (!user?.managementUser) return;
    
    const rawRole = user.managementUser.role;
    const role = rawRole ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase() : null;
    
    if (savedTenant && role === 'Admin') {
      setSelectedTenantState(savedTenant);
    } else if (savedTenant && (role === 'Corporate' || role === 'Manager')) {
      // For Corporate/Manager users, only restore if it matches their assigned tenant
      if (savedTenant === user.managementUser.tenant_id) {
        setSelectedTenantState(savedTenant);
      } else {
        localStorage.removeItem('selectedTenant');
      }
    }
  }, [user?.managementUser]);

  const setSelectedTenant = async (tenantId: string | null) => {
    setSelectedTenantState(tenantId);
    if (tenantId) {
      // Fetch tenant details when a tenant is selected
      setIsLoading(true);
      try {
        const response = await managementService.getTenantById(tenantId);
        if (response.success && response.tenant) {
          setTenant({
            tenant_id: response.tenant.tenant_id,
            tenant_name: response.tenant.tenant_name,
            description: response.tenant.description,
            created: response.tenant.created,
            metric_labels: parseMetricLabels(response.tenant.metric_labels)
          });
        } else {
          console.error('Error fetching tenant details:', response.error);
          // Fallback if tenant not found
          setTenant({
            tenant_id: tenantId,
            tenant_name: `Tenant ${tenantId}`,
            description: '',
            created: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('Error fetching tenant details:', error);
        // Fallback on error
        setTenant({
          tenant_id: tenantId,
          tenant_name: `Tenant ${tenantId}`,
          description: '',
          created: new Date().toISOString()
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      setTenant(null);
    }
  };

  // Fetch role metric configs when tenant changes
  useEffect(() => {
    if (!selectedTenant || fetchedRoleConfigsTenantRef.current === selectedTenant) return;
    const fetchRoleConfigs = async () => {
      try {
        const roles = await promptApi.listRoles(selectedTenant);
        const configs: Record<string, RoleMetricConfig> = {};
        for (const role of roles) {
          try {
            const config = await promptApi.getMetricConfig(selectedTenant, role.role_key);
            configs[role.role_key] = config;
          } catch (e) {
            console.warn(`Failed to fetch metric config for role ${role.role_key}:`, e);
          }
        }
        setRoleMetricConfigs(configs);
        fetchedRoleConfigsTenantRef.current = selectedTenant;
      } catch (e) {
        // No prompt templates configured yet — that's fine
        console.debug('No role configs available:', e);
        setRoleMetricConfigs({});
        fetchedRoleConfigsTenantRef.current = selectedTenant;
      }
    };
    fetchRoleConfigs();
  }, [selectedTenant]);

  const getMetricConfigForRole = useCallback((role: string | null): RoleMetricConfig | null => {
    if (!role) return null;
    return roleMetricConfigs[role] || null;
  }, [roleMetricConfigs]);

  const fetchVersionConfigs = useCallback(async (versionIds: string[]) => {
    // Filter to only IDs we haven't fetched yet
    const missing = versionIds.filter(id => id && !versionMetricConfigs[id]);
    if (missing.length === 0) return;

    const newConfigs: Record<string, RoleMetricConfig> = {};
    for (const versionId of missing) {
      try {
        const config = await promptApi.getMetricConfigByVersion(versionId);
        newConfigs[versionId] = config;
      } catch (e) {
        console.warn(`Failed to fetch metric config for version ${versionId}:`, e);
      }
    }
    if (Object.keys(newConfigs).length > 0) {
      setVersionMetricConfigs(prev => ({ ...prev, ...newConfigs }));
    }
  }, [versionMetricConfigs]);

  const getConfigForShift = useCallback((shift: { prompt_template_version_id?: string; role?: string | null }): RoleMetricConfig | null => {
    // Tier 1: version-pinned config
    if (shift.prompt_template_version_id && versionMetricConfigs[shift.prompt_template_version_id]) {
      return versionMetricConfigs[shift.prompt_template_version_id];
    }
    // Tier 2: current active config for role
    if (shift.role) {
      const roleConfig = roleMetricConfigs[shift.role];
      if (roleConfig) return roleConfig;
    }
    // Tier 3: null — components fall back to SAJJ defaults
    return null;
  }, [versionMetricConfigs, roleMetricConfigs]);

  // Clear version config cache when tenant changes
  useEffect(() => {
    setVersionMetricConfigs({});
  }, [selectedTenant]);

  // If the tenant supplies its own metric_labels (including role-specific
  // keys like ``add_on_upsell``), trust it as-is. Merging with defaults
  // would drag the legacy ``metric_1/2/3`` keys along and surface ghost
  // columns on the Server Scorecard table.
  const metricLabels: MetricLabels = tenant?.metric_labels
    ? tenant.metric_labels
    : DEFAULT_METRIC_LABELS;

  const value: TenantContextType = {
    selectedTenant,
    setSelectedTenant,
    tenantName,
    tenant,
    isLoading,
    metricLabels,
    roleMetricConfigs,
    getMetricConfigForRole,
    versionMetricConfigs,
    fetchVersionConfigs,
    getConfigForShift,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};
